import React, {useEffect } from "react";
import { toast } from "react-toastify";
import {
  useUpdateEmployeeProfileMutation,
  useFetchCurrentUserQuery,
} from "../../../services/profileService";
import useAuth from "../../../hooks/useAuth";
import {
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  styled,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// cimport * as yup from 'yup';

const validationSchema = yup.object().shape({
  job_title: yup.string().required("Job title is required"),
  manager_name: yup.string().required("Manager name is required"),
  manager_email: yup.string().email("Invalid email").required("Manager email is required"),
  company_name: yup.string().required("Company name is required"),
  purpose: yup
    .string()
    .required("Purpose is required")
    .oneOf(
      [
        "New Role Design/Development",
        "Onboarding",
        "Performance Management",
        "Succession Planning/Retirement",
        "Delegate & Elevate",
      ],
      "Invalid purpose"
    ),
});

const BasicInfo = () => {
  const navigate = useNavigate();
  const { users } = useAuth();  // Assuming `useAuth()` gives you the current logged-in user
  const employeeId = users?.user_id;

  // Form state
  const { handleSubmit, control, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      job_title: "",
      manager_name: "",
      manager_email: "",
      company_name: "",
      purpose: "",
    },
  });

  // Fetch current user data
  const { data: currentUser, isLoading: isLoadingUser, refetch } = useFetchCurrentUserQuery(employeeId);

  useEffect(() => {
    if (currentUser) {
      setValue("job_title", currentUser?.data?.job_title);
      setValue("manager_name", currentUser?.data?.manager_name);
      setValue("manager_email", currentUser?.data?.manager_email);
      setValue("company_name", currentUser?.data?.company_name);
      setValue("purpose", currentUser?.data?.purpose);
    }
  }, [currentUser, setValue]);

  // Mutation for updating profile
  const [updateEmployeeProfile, { isLoading: isUpdating, isSuccess, isError }] = useUpdateEmployeeProfileMutation();

  const onSubmit = async (data) => {
    try {
      await updateEmployeeProfile({ user_id: employeeId, data }).unwrap();
      toast.success("Profile updated successfully!");
      if (isSuccess) navigate("/preview");
    } catch (error) {
      toast.error("Failed to update profile. Please try again.");
    }
  };

  if (isLoadingUser) {
    return <CircularProgress />;
  }

  return (
    <SectionWrap component="section">
      <ContainerWrap>
        <RowWrap>
          <Col12Wrap>
            <FormFirstBoxWrap>
              <TitleWrap>Add Basic Info to Complete Profile</TitleWrap>
            </FormFirstBoxWrap>
          </Col12Wrap>
          <Col12Wrap>
            <FormMiddleBoxWrap>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ maxWidth: "400px", margin: "auto" }}>
                  {isSuccess && <Alert severity="success">Profile updated successfully!</Alert>}
                  {isError && <Alert severity="error">Failed to update profile.</Alert>}

                  {/* Prepared By */}
                  <FormGroupWrap>
                    <FormLabelWrap>Prepared By</FormLabelWrap>
                    <TextField
                      fullWidth
                      value={`${currentUser?.data?.name} (${currentUser?.data?.email})`}
                      disabled
                      id="outlined-disabled"
                      variant="filled"
                      sx={{
                        width: "100%",
                        border: "1px solid #DADADA",
                        borderRadius: "7px",
                      }}
                    />
                  </FormGroupWrap>

                  {/* Job Title */}
                  <FormGroupWrap>
                    <FormLabelWrap>Job Title</FormLabelWrap>
                    <Controller
                      name="job_title"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Enter Job Title"
                          error={!!errors.job_title}
                          helperText={errors.job_title?.message}
                          sx={{ width: "100%" }}
                        />
                      )}
                    />
                  </FormGroupWrap>

                  {/* Manager Name */}
                  <FormGroupWrap>
                    <FormLabelWrap>Manager Name</FormLabelWrap>
                    <TextField
                      fullWidth
                      value={currentUser?.data?.manager_name || ""}
                      disabled
                      id="outlined-disabled"
                      variant="filled"
                      sx={{ width: "100%" }}
                    />
                  </FormGroupWrap>

                  {/* Manager Email */}
                  <FormGroupWrap>
                    <FormLabelWrap>Manager's Email</FormLabelWrap>
                    <TextField
                     disabled
                     id="outlined-disabled"
                     variant="filled"
                      fullWidth
                      value={currentUser?.data?.manager_email || ""}
                    
                      sx={{ width: "100%" }}
                    />
                  </FormGroupWrap>

                  {/* Company Name */}
                  <FormGroupWrap>
                    <FormLabelWrap>Company Name</FormLabelWrap>
                    <TextField
                      disabled
                      id="outlined-disabled"
                      variant="filled"
                      fullWidth
                      value={currentUser?.data?.company_name || ""}
                      sx={{
                        width: "100%",
                        "& .MuiFilledInput-root": {
                          borderBottom: "none",  // Removes the bottom line in filled variant
                        },
                      }}
                    />
                  </FormGroupWrap>

                  {/* Purpose Dropdown */}
                  <FormGroupWrap>
                    <FormLabelWrap>Purpose</FormLabelWrap>
                    <Controller
                      name="purpose"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          fullWidth
                      
                          error={!!errors.purpose}
                          helperText={errors.purpose?.message}
                        >
                          <MenuItem value="">Select Purpose</MenuItem>
                          <MenuItem value="New Role Design/Development">New Role Design/Development</MenuItem>
                          <MenuItem value="Onboarding">Onboarding</MenuItem>
                          <MenuItem value="Performance Management">Performance Management</MenuItem>
                          <MenuItem value="Succession Planning/Retirement">Succession Planning/Retirement</MenuItem>
                          <MenuItem value="Delegate & Elevate">Delegate & Elevate</MenuItem>
                        </TextField>
                      )}
                    />
                  </FormGroupWrap>

                  <FormGroupWrap>
                    <CustomBtn
                      type="submit"
                      variant="outlined"
                      disabled={isUpdating}
                    >
                      {isUpdating ? <CircularProgress size={24} /> : "Save"}
                    </CustomBtn>
                  </FormGroupWrap>
                </Box>
              </form>
            </FormMiddleBoxWrap>
          </Col12Wrap>
        </RowWrap>
      </ContainerWrap>
    </SectionWrap>
  );
};

export default BasicInfo;


// const BasicInfo = () => {
//   const navigate = useNavigate();
//   const { users } = useAuth();
//   const employeeId = users?.user_id;

//   const [managers, setManagers] = useState([]);
//   const [selectedManagerEmail, setSelectedManagerEmail] = useState("");
  

//   const { register, handleSubmit, control, setValue, formState: { errors } } = useForm({
//     mode: "onChange",
//     defaultValues: {
//       job_title: "",
//       manager_id: "",
//       company_name: "",
//       purpose: "",
//       prepared_by_name: "",
//       prepared_by_email: "",
//     },
//     resolver: yupResolver(validationSchema),
//   });

//   // Fetch current user data
//   const { data: currentUser, isLoading: isLoadingUser } = useFetchCurrentUserQuery(employeeId);

//   useEffect(() => {
//     if (currentUser) {
//       setValue("prepared_by_name", currentUser?.data?.name);
//       setValue("prepared_by_email", currentUser?.data?.email);
//     }
//   }, [currentUser, setValue]);

//   // Fetch managers
//   const { data: managersResponse, isLoading: isLoadingManagers, refetch } = useFetchManagersQuery();

//   useEffect(() => {
//     refetch(); // Force a re-fetch on component mount
//     if (managersResponse) {
//       setManagers(Array.isArray(managersResponse) ? managersResponse : managersResponse?.data);
//     }
//   }, [managersResponse]);

//   // Update employee profile mutation
//   const [updateEmployeeProfile, { isLoading: isUpdating, isSuccess, isError }] = useUpdateEmployeeProfileMutation();

//   const onSubmit = async (data) => {
//     try {
//       const response = await updateEmployeeProfile({ user_id: employeeId, data }).unwrap();
//       toast.success("Profile updated successfully!");
//       // if (isSuccess) navigate("/dashboard");
//       if (isSuccess) navigate("/preview");
//     } catch (error) {
//       toast.error("Failed to update profile. Please try again.");
//     }
//   };

//   if (isLoadingUser || isLoadingManagers) {
//     return <CircularProgress />;
//   }

//   return (
//     <SectionWrap component="section">
//       <ContainerWrap>
//         <RowWrap>
//           <Col12Wrap>
//             <FormFirstBoxWrap>
//               <TitleWrap>Add Basic Info to Complete Profile</TitleWrap>
//             </FormFirstBoxWrap>
//           </Col12Wrap>
//           <Col12Wrap>
//             <FormMiddleBoxWrap>
//               <form onSubmit={handleSubmit(onSubmit)}>
//                 <Box sx={{ maxWidth: "400px", margin: "auto" }}>
//                   {isSuccess && (
//                     <FormGroupWrap>
//                       <Alert severity="success">Profile updated successfully!</Alert>
//                     </FormGroupWrap>
//                   )}
//                   {isError && (
//                     <FormGroupWrap>
//                       <Alert severity="error">Failed to update profile.</Alert>
//                     </FormGroupWrap>
//                   )}

//                   {/* Prepared By */}
//                   <FormGroupWrap>
//                     <FormLabelWrap>Prepared By</FormLabelWrap>
//                     <TextField
//                       fullWidth
//                       value={`${currentUser?.data?.name} (${currentUser?.data?.email})`}
//                       disabled
//                       sx={{
//                         width: "100%",
//                         border: "1px solid #DADADA",
//                         borderRadius: "7px",
//                       }}
//                     />
//                   </FormGroupWrap>

//                   {/* Job Title */}
//                   <FormGroupWrap>
//                     <FormLabelWrap>Job title</FormLabelWrap>
//                     <Controller
//                       name="job_title"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           placeholder="Enter Job Title"
//                           error={!!errors.job_title}
//                           helperText={errors.job_title?.message}
//                           sx={{ width: "100%"}}
//                         />
//                       )}
//                     />
//                   </FormGroupWrap>

//                   {/* Manager */}
//                   <FormGroupWrap>
//                       <FormLabelWrap>Manager</FormLabelWrap>
//                       <Controller
//                         name="manager_id"
//                         control={control}
//                         render={({ field }) => (
//                           <TextField
//                             {...field}
//                             select
//                             fullWidth
//                             error={!!errors.manager_id}
//                             helperText={errors.manager_id?.message}
//                             sx={{ width: "100%" }}
//                             onChange={(e) => {
//                               const managerId = e.target.value;
//                               field.onChange(managerId); // Update form state
//                               const selectedManager = managers.find((manager) => manager.id === managerId);
//                               setSelectedManagerEmail(selectedManager?.email || ""); // Update email state
//                             }}
//                           >
//                             <MenuItem value="">Select Manager</MenuItem>
//                             {managers.map((manager) => (
//                               <MenuItem key={manager.id} value={manager.id}>
//                                 {manager.name}
//                               </MenuItem>
//                             ))}
//                           </TextField>
//                         )}
//                       />
//                     </FormGroupWrap>

//                     <FormGroupWrap>
//                       <FormLabelWrap>Manager's Email</FormLabelWrap>
//                       <TextField
//                         fullWidth
//                         value={selectedManagerEmail || ""}
//                         disabled
//                         sx={{ width: "100%" }}
//                       />
//                     </FormGroupWrap>

//                   {/* Company Name */}
//                   <FormGroupWrap>
//                     <FormLabelWrap>Company Name</FormLabelWrap>
//                     <Controller
//                       name="company_name"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           placeholder="Enter Company Name"
//                           error={!!errors.company_name}
//                           helperText={errors.company_name?.message}
//                           sx={{ width: "100%" }}
//                         />
//                       )}
//                     />
//                   </FormGroupWrap>

//                   {/* Purpose Dropdown */}
//                   <FormGroupWrap>
//                     <FormLabelWrap>Purpose</FormLabelWrap>
//                     <Controller
//                       name="purpose"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           select
//                           fullWidth
//                           error={!!errors.purpose}
//                           helperText={errors.purpose?.message}
//                           sx={{ width: "100%" }}
//                         >
//                           <MenuItem value="">Select Purpose</MenuItem>
//                           <MenuItem value="New Role Design/Development">New Role Design/Development</MenuItem>
//                           <MenuItem value="Onboarding">Onboarding</MenuItem>
//                           <MenuItem value="Performance Management">Performance Management</MenuItem>
//                           <MenuItem value="Succession Planning/Retirement">Succession Planning/Retirement</MenuItem>
//                           <MenuItem value="Delegate & Elevate">Delegate & Elevate</MenuItem>
//                         </TextField>
//                       )}
//                     />
//                   </FormGroupWrap>

//                   <FormGroupWrap>
//                     <CustomBtn
//                       type="submit"
//                       variant="outlined"
//                       disabled={isUpdating}
//                     >
//                       {isUpdating ? <CircularProgress size={24} /> : "Save"}
//                     </CustomBtn>
//                   </FormGroupWrap>
//                 </Box>
//               </form>
//             </FormMiddleBoxWrap>
//           </Col12Wrap>
//         </RowWrap>
//       </ContainerWrap>
//     </SectionWrap>
//   );
// };

// export default BasicInfo;

const SectionWrap = styled(Box)(() => ({
  padding: "30px 0 100px",
}));
const ContainerWrap = styled(Box)(() => ({
  width: "100%",
  maxWidth: "1170px",
  paddingLeft: "15px",
  paddingRight: "15px",
  marginLeft: "auto",
  marginRight: "auto",
}));
const RowWrap = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-15px",
  marginRight: "-15px",
}));
const Col12Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
}));
const FormFirstBoxWrap = styled(Box)(() => ({
  padding: "20px 150px",
  background: "#ffffff",
  maxWidth: "1030px",
  borderTop: "2px solid #1085AB",
  borderRadius: "7px 7px 0px 0px",
  marginLeft: "auto",
  marginRight: "auto",
  marginBottom: "16px",
  boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.05)",
  textAlign: "center",
  "@media (max-width: 1199px)": {
    maxWidth: "100%",
    paddingLeft: "100px",
    paddingRight: "100px",
  },
  "@media (max-width: 991px)": {
    paddingLeft: "50px",
    paddingRight: "50px",
  },
  "@media (max-width: 575px)": {
    paddingLeft: "15px",
    paddingRight: "15px",
  },
}));
const FormMiddleBoxWrap = styled(Box)(() => ({
  padding: "50px 150px",
  background: "#ffffff",
  borderRadius: "0px 0px 7px 7px",
  maxWidth: "1030px",
  marginLeft: "auto",
  marginRight: "auto",
  marginBottom: "16px",
  boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.05)",
  "@media (max-width: 1199px)": {
    maxWidth: "100%",
    paddingLeft: "100px",
    paddingRight: "100px",
  },
  "@media (max-width: 991px)": {
    paddingLeft: "50px",
    paddingRight: "50px",
  },
  "@media (max-width: 575px)": {
    paddingLeft: "15px",
    paddingRight: "15px",
  },
}));
const TitleWrap = styled(Typography)(() => ({
  fontSize: "28px",
  fontWeight: "600",
  lineHeight: "1.445",
  color: "#000000",
}));
const FormGroupWrap = styled(Box)(() => ({
  marginBottom: "20px",
  position: "relative",
}));
const CustomBtn = styled(Button)(() => ({
  width: "100%",
  fontSize: "20px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#F28021",
  borderColor: "#F28021",
  borderRadius: "10px",
  padding: "15px 20px",
  boxShadow: "none !important",
}));
const FormLabelWrap = styled(Typography)(() => ({
  fontSize: "18px",
  fontWeight: "400",
  lineHeight: "1.445",
  color: "#2C2C2E",
  marginBottom: "5px",
}));
